import { format } from "date-fns";
import { Calendar } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Coupon } from "@/pages/coupons/types/coupon";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";

interface CouponCardProps {
  coupons: Coupon[];
  setSelectedCoupon: (value: any) => void;
  setShowCouponDrawer: (value: boolean) => void;
  // Owned by the page (single source of truth) — the card only reports which
  // action the user took: Add = create, card click = edit, Duplicate = duplicate.
  setFormMode: (mode: "create" | "edit" | "duplicate") => void;
  // Sanitize-and-open lives in the page so the copy rules are shared with the
  // Duplicate button inside the edit form.
  onDuplicate: (coupon: any) => void;
}

export function CouponCard({
  coupons,
  setSelectedCoupon,
  setShowCouponDrawer,
  setFormMode,
  onDuplicate,
}: CouponCardProps) {
  const getDiscountText = (coupon: Coupon) => {
    if (coupon.discount_percentage) {
      return `${coupon.currency}\t${coupon.discount_percentage}% off`;
    }
    if (coupon.discount_amount) {
      return `${coupon.currency}\t${coupon.discount_amount} off`;
    }
    return "Special discount";
  };

  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "active";
      case "expired":
        return "destructive";
      case "inactive":
        return "secondary";
      default:
        return "default";
    }
  };

  return (
    <div className="space-y-6 w-full">
      <Card className="overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-3xl">Coupons</CardTitle>
              <CardDescription className="text-sm">All Coupons</CardDescription>
            </div>
            <HasAccess permission="write">
              <Button
                className="bg-sidebar"
                onClick={() => {
                  setSelectedCoupon(null);
                  setShowCouponDrawer(true);
                  setFormMode("create");
                }}
              >
                Add Coupon
              </Button>
            </HasAccess>
          </div>
        </CardHeader>
        <ScrollArea className="h-[calc(100%-5rem)]">
          <CardContent className="space-y-10">
            {coupons.map((coupon) => (
              <Card
                key={coupon.id}
                className="cursor-pointer"
                onClick={() => {
                  setSelectedCoupon(coupon);
                  setShowCouponDrawer(true);
                  setFormMode("edit");
                }}
              >
                <CardHeader className="bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <CardTitle className="text-lg font-semibold">
                        {coupon.name}
                      </CardTitle>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {format(new Date(coupon.start_date), "PPP")}
                          {coupon.end_date && (
                            <> - {format(new Date(coupon.end_date), "PPP")}</>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between ">
                      {coupon.first_time_user_only && (
                        <Badge variant="gifted" className="w-fit mr-2">
                          FIRST TIME USERS ONLY
                        </Badge>
                      )}
                      <Badge variant={getStatusVariant(coupon.status)}>
                        {coupon.status.toUpperCase()}
                      </Badge>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          // stopPropagation: the whole card opens EDIT on click —
                          // without it the duplicate would be overridden.
                          e.stopPropagation();
                          onDuplicate(coupon); // page sanitizes + opens drawer
                        }}
                        className="ml-2 bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200"
                      >
                        Duplicate
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid gap-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-muted-foreground">
                          {getDiscountText(coupon)}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {coupon.type.replace("_", " ")}
                      </div>
                    </div>

                    {(coupon.discount_min ||
                      coupon.discount_cap ||
                      coupon.usage_limit !== null) && (
                      <div className="grid gap-2 text-sm">
                        {coupon.discount_min && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Min. Order Amount
                            </span>
                            <span>
                              {coupon.currency + "\t" + coupon.discount_min}
                            </span>
                          </div>
                        )}
                        {coupon.discount_cap && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Max. Discount
                            </span>
                            <span>
                              {coupon.currency + "\t" + coupon.discount_cap}
                            </span>
                          </div>
                        )}
                        {coupon.usage_limit !== null && coupon.usage_limit !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Usage
                            </span>
                            <span className={coupon.usage_count >= coupon.usage_limit ? "text-red-500 font-medium" : ""}>
                              {coupon.usage_count} / {coupon.usage_limit}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </ScrollArea>
      </Card>
    </div>
  );
}
